import { motion } from 'framer-motion';
import { MapPin, Globe } from 'lucide-react';
import { EXPERTISE_DATA, translations } from '@/lib/index';
import { useLanguage, Layout } from '@/components/Layout';
import { IMAGES } from '@/assets/images';

export default function Expertise() {
  const { language } = useLanguage();
  const t = translations[language];

  const continents = [
    {
      name: t.expertise.asia,
      cities: EXPERTISE_DATA.asia,
      gradient: 'from-primary/10 to-accent/10'
    },
    {
      name: t.expertise.europe,
      cities: EXPERTISE_DATA.europe,
      gradient: 'from-accent/10 to-secondary/10'
    },
    {
      name: t.expertise.africa,
      cities: EXPERTISE_DATA.africa,
      gradient: 'from-secondary/10 to-primary/10'
    }
  ];

  return (
    <Layout>
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.HERO_CONSULTING_3}
            alt="International Expertise"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Globe className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium text-primary">20+ {t.expertise.years}</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              {t.expertise.title}
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t.expertise.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-b from-background via-muted/20 to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-4">
              {language === 'fr' && 'Présence Internationale'}
              {language === 'en' && 'International Presence'}
              {language === 'es' && 'Presencia Internacional'}
              {language === 'zh' && '国际存在'}
              {language === 'ar' && 'التواجد الدولي'}
            </h2>
            <p className="text-xl text-muted-foreground">
              {language === 'fr' && '18 villes clés sur 3 continents'}
              {language === 'en' && '18 key cities across 3 continents'}
              {language === 'es' && '18 ciudades clave en 3 continentes'}
              {language === 'zh' && '3大洲的18个关键城市'}
              {language === 'ar' && '18 مدينة رئيسية عبر 3 قارات'}
            </p>
          </motion.div>
          
          {/* Global Network Map */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <div className="relative rounded-3xl overflow-hidden border-2 border-primary/20 shadow-2xl">
              <img
                src={IMAGES.GLOBAL_NETWORK_MAP}
                alt="Global Cities Network - Kyra AVM Consulting International Presence"
                className="w-full h-auto"
              />
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {continents.map((continent, index) => (
              <motion.div
                key={continent.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group"
              >
                <div className={`relative h-full bg-gradient-to-br ${continent.gradient} rounded-3xl border-2 border-border overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-primary/20 hover:border-primary/50 hover:-translate-y-3`}>
                  <div className="absolute top-0 right-0 w-40 h-40 bg-primary/10 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-accent/10 rounded-full blur-2xl" />
                  <div className="relative p-8">
                    <div className="flex items-center justify-between mb-8">
                      <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <Globe className="w-7 h-7 text-primary" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold group-hover:text-primary transition-colors duration-300">{continent.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            {continent.cities.length} {language === 'fr' ? 'villes' : language === 'en' ? 'cities' : language === 'es' ? 'ciudades' : language === 'zh' ? '城市' : 'مدن'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {continent.cities.map((location, cityIndex) => (
                        <motion.div
                          key={cityIndex}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.3, delay: cityIndex * 0.05 }}
                          className="flex items-start gap-3 p-4 rounded-xl bg-background/60 backdrop-blur-sm border border-border/50 transition-all duration-300 hover:bg-card hover:border-primary/40 hover:shadow-lg hover:scale-[1.02] cursor-pointer group/city"
                        >
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover/city:bg-primary/20 transition-colors duration-300">
                            <MapPin className="w-5 h-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-foreground text-lg group-hover/city:text-primary transition-colors duration-300">{location.city}</p>
                            <p className="text-sm text-muted-foreground">{location.country}</p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-gradient-to-br from-primary/5 via-background to-accent/5">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="p-10 rounded-3xl bg-gradient-to-br from-card to-card/50 border-2 border-border shadow-xl hover:shadow-2xl hover:border-primary/50 transition-all duration-300 hover:-translate-y-2"
              >
                <div className="text-6xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent mb-3">20+</div>
                <p className="text-lg text-muted-foreground font-medium">{t.expertise.years}</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="p-10 rounded-3xl bg-gradient-to-br from-card to-card/50 border-2 border-border shadow-xl hover:shadow-2xl hover:border-primary/50 transition-all duration-300 hover:-translate-y-2"
              >
                <div className="text-6xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent mb-3">18</div>
                <p className="text-lg text-muted-foreground font-medium">{t.expertise.countries}</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="p-10 rounded-3xl bg-gradient-to-br from-card to-card/50 border-2 border-border shadow-xl hover:shadow-2xl hover:border-primary/50 transition-all duration-300 hover:-translate-y-2"
              >
                <div className="text-6xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent mb-3">3</div>
                <p className="text-lg text-muted-foreground font-medium">Continents</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
